import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { DynamicDialogModule, DialogService } from 'primeng/dynamicdialog';
import { AddTableComponent } from '../add-table/add-table.component';
import { MessageService } from 'primeng/api';
import {
  Restaurant,
  RestaurantWithTables,
  RestaurantTable,
  CreateRestaurant,
} from '../../_models/Restaurant';
import { ApiService } from '../../_services';
import { SpinnerService } from '../spinner/spinner.service';
import { Constants } from '../../_utility';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';

@Component({
  selector: 'app-restaurant-form',
  templateUrl: './restaurant-form.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrl: './restaurant-form.component.scss',
  standalone: true,
  imports: [TableModule, ButtonModule, InputText, InputNumber, Select, Tooltip, ReactiveFormsModule, CommonModule, TranslateModule, DynamicDialogModule],
})
export class RestaurantFormComponent implements OnInit, OnChanges {
  public form: FormGroup;
  @Input() ships: any[] = [];
  @Input() edit: boolean = false;
  @Input() selectedRestaurant!: Restaurant;
  public tables: RestaurantTable[] = [];
  @Output() restaurantCreated = new EventEmitter<boolean>();
  @Output() restaurantUpdated = new EventEmitter<boolean>();
  @ViewChild(Table) tablesTable?: Table;
  private restaurant: RestaurantWithTables | undefined;
  constructor(
    private fb: FormBuilder,
    private dialog: DialogService,
    private apiService: ApiService,
    private spinner: SpinnerService,
    public router: Router,
    private cdRef: ChangeDetectorRef,
    public translate: TranslateService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      global_id_in: ['', [Validators.required]],
      global_id_out: ['', [Validators.required]],
      img_url: [''],
      myitalian_percentage: [
        0,
        [Validators.required, Validators.min(0), Validators.minLength(0)],
      ],
      fk_ship: ['', [Validators.required]],
      info_deck: [''],
    });
  }

  ngOnInit() {
    if (this.edit) {
      this.initEdit();
    }
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedRestaurant']?.currentValue) {
      this.initEdit();
    }
  }

  initEdit() {
    this.spinner.show();
    this.apiService
      .Restaurant(this.selectedRestaurant.id_restaurant)
      .subscribe((response) => {
        this.restaurant = response;
        this.populateForm();
        this.spinner.hide();
      });
  }
  populateForm() {
    this.form = this.fb.group({
      name: [this.restaurant!.name, [Validators.required]],
      global_id_in: [this.restaurant!.global_id_in, [Validators.required]],
      global_id_out: [this.restaurant!.global_id_out, [Validators.required]],
      img_url: [this.restaurant!.img_url],
      myitalian_percentage: [
        this.restaurant!.myitalian_percentage,
        [Validators.required, Validators.min(0), Validators.minLength(0)],
      ],
      fk_ship: [this.restaurant!.fk_ship, [Validators.required]],
      info_deck: [this.restaurant!.info_deck],
    });
    this.tables = this.restaurant!.tables;
  }

  resetForm() {
    // Reset the form to initial state
    this.form.reset();
    // Clear tables array
    this.tables = [];
  }

  AddTable() {
    const ref = this.dialog.open(AddTableComponent, {
      styleClass: 'resizableDialog',
      header: this.translate.instant('confirm_dialog.add_table_title'),
      width: '50vw',
      modal: true,
      closable: true,
    });

    ref?.onClose.subscribe((tableData: any) => {
      if (tableData) {
        this.tables = [...this.tables, tableData];
        this.tablesTable?.reset();
      }
    });
  }

  editTable(table: RestaurantTable) {
    const ref = this.dialog.open(AddTableComponent, {
      styleClass: 'resizableDialog',
      header: this.translate.instant('confirm_dialog.edit_table_title'),
      width: '50vw',
      modal: true,
      closable: true,
      data: table,
    });

    ref?.onClose.subscribe((tableData: RestaurantTable) => {
      if (tableData) {
        table.name = tableData.name;
        table.capacity = tableData.capacity;
        table.min_limit = tableData.min_limit;
        table.fk_status = tableData.fk_status;
        this.tablesTable?.reset();
      }
    });
  }
  activateTable(product: RestaurantTable) {
    if (this.edit) {
      product.fk_status = 9;
    }
  }

  deactivateTable(product: RestaurantTable) {
    if (this.edit) {
      product.fk_status = 10;
    }
  }

  deleteTable(table: RestaurantTable, index: number) {
    this.tables = this.tables.filter(
      (t) =>
        t.name !== table.name ||
        t.id_table !== table.id_table ||
        t.fk_status !== table.fk_status ||
        t.capacity !== table.capacity ||
        t.min_limit !== table.min_limit
    );
    this.tablesTable?.reset();
    this.cdRef.detectChanges();
  }

  createRestaurant() {
    const payload: CreateRestaurant = {
      restaurant: this.form.value,
      tables: this.tables,
    };
    this.apiService.CreateRestaurant(payload).subscribe({
      next: (response) => {
        this.restaurantCreated.emit(true);
        this.resetForm();
      },
      error: (error) => {
        console.log('Restaurant form error:', error);
        this.restaurantCreated.emit(false);
      },
    });
  }
  editRestaurant() {
    this.spinner.show();
    let payload: CreateRestaurant = {
      restaurant: this.form.value,
      tables: this.tables,
    };
    payload.restaurant.fk_ship = this.selectedRestaurant.fk_ship;
    this.apiService
      .UpdateRestaurant(this.selectedRestaurant.id_restaurant, payload)
      .subscribe({
        next: (response) => {
          this.restaurantUpdated.emit(true);
          this.resetForm();
          this.spinner.hide();
        },
        error: (error) => {
          console.log('Restaurant form error:', error);
          this.restaurantUpdated.emit(false);
        },
      });
  }

  Back() {
    this.goTo('restaurant_management');
  }
  goTo(path: string) {
    switch (path) {
      case 'home': {
        this.router.navigate(Constants.Routing.HOME.routerLink);
        break;
      }
      case 'restaurant_management': {
        this.router.navigate(Constants.Routing.HOME_RISTORANTI.routerLink);
        break;
      }
    }
  }
}
